import { computed, inject, Injectable, signal } from '@angular/core';

import { catchError, map, Observable, of, shareReplay, tap } from 'rxjs';

import { MpsApiService } from '../../mps/services/mps-api.service';
import { applicationEndpoints } from '../application-endpoints';
import { AppConfig } from '../interfaces/app-config';
import { LaravelDefaultResponse } from '../interfaces/responses';

/**
 * Holds deploy-time backend configuration (`GET /app-config`) in memory for the app's lifetime.
 *
 * Loaded once at bootstrap via an app initializer, and lazily backfilled by `ensureLoaded()` for
 * any authenticated view that needs it (covers the first-login window where the bootstrap fetch is
 * skipped because no token exists yet). Values are the single source of truth — consumers read them
 * here instead of hardcoding a parallel copy that would drift from the backend.
 */
@Injectable({
  providedIn: 'root',
})
export class AppConfigService {
  private readonly apiService = inject(MpsApiService);

  private readonly _config = signal<AppConfig | null>(null);

  /** In-memory app config; null until the first successful load. */
  readonly config = this._config.asReadonly();

  /** Archival window in days, or null before config has loaded. */
  readonly archivalDays = computed<number | null>(() => this._config()?.archival_days ?? null);

  private inFlight$: Observable<AppConfig | null> | null = null;

  /**
   * Fetch the config and cache it. Already-loaded calls short-circuit; concurrent calls share one
   * request. Resolves to null on error (config left unset, in-flight cleared so a later call retries)
   * so a flaky endpoint can never block bootstrap.
   */
  load(): Observable<AppConfig | null> {
    if (this._config() !== null) {
      return of(this._config());
    }
    if (this.inFlight$) {
      return this.inFlight$;
    }

    this.inFlight$ = this.apiService.get<LaravelDefaultResponse<AppConfig>>(applicationEndpoints.appConfig).pipe(
      map((response) => response.data),
      tap((config) => this._config.set(config)),
      catchError(() => {
        this.inFlight$ = null;
        return of(null);
      }),
      shareReplay(1)
    );

    return this.inFlight$;
  }

  /** Best-effort load if not already in memory — safe to call from any authenticated view. */
  ensureLoaded(): void {
    if (this._config() === null) {
      this.load().subscribe();
    }
  }
}
